import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';

import { UserDetails } from '../models/user.model';
import { BaseService } from './base.service';

import {
    SearchResult,
    Asset,
    Tool,
    ToolCategory,
    ToolSubcategory,
    ToolFilter,
    Attachment,
    ToolDeliverableType,
    ToolParameterType,
    GeneralSettings,
    PartSupplierDetails,
    Part,
    SupplierCommunicationValidation
} from '../models/generic.model';
import {
    Profile,
    Location,
    OwnershipType,
    SupplierWarehouse,
    Certification,
    CertificationType,
    ProfileAttachment,
    CertificationAttachment,
    PickupWarehouse,
    Supplier,
    RequestListItem
} from '../models/profile.model';
import { SupplierAddressPropertyValues, SupplierDocument } from '../models/supplier.model';
import { map } from 'rxjs/operators';
import { AppSettings } from '../../app.settings';

@Injectable()
export class ProfileService extends BaseService {
    constructor(private http: HttpClient, private config: AppSettings) {
        super();
    }

    getGeneralSettings(): Observable<GeneralSettings> {
        const url = `${this.config.BaseUrl}Profile/Settings`;
        return this.http.get<GeneralSettings>(url, { headers: this.setHeaders() });
    }

    getUserDetails(): Observable<UserDetails> {
        const url = `${this.config.BaseUrl}Profile/GetUserDetails`;
        let params = new HttpParams();
        return this.http.get<UserDetails>(url, { headers: this.setHeaders(), params: params });
    }

    searchSupplier(searchTerm: string, take: number): Observable<Supplier[]> {
        const request = {
            searchTerm: searchTerm,
            take: take
        };

        const url = `${this.config.BaseUrl}Profile/Supplier`;
        return this.http.post<Supplier[]>(url, request, { headers: this.setHeaders() });
    }

    getBasicInfo(supplierCode: string): Observable<Profile> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}`;
        return this.http.get<Profile>(url, { headers: this.setHeaders() });
    }

    postBasicInfo(supplierCode: string, model: Profile): Observable<Profile> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}`;
        return this.http.post<Profile>(url, model, { headers: this.setHeaders() });
    }

    putBasicInfo(supplierCode: string, model: Profile): Observable<Profile> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}`;
        return this.http.put<Profile>(url, model, { headers: this.setHeaders() });
    }

    getDocuments(supplierCode: string): Observable<SupplierDocument[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Document`;
        return this.http.get<SupplierDocument[]>(url, { headers: this.setHeaders() });
    }

    getLocations(supplierCode: string): Observable<Location[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Location`;
        return this.http.get<Location[]>(url, { headers: this.setHeaders() });
    }

    getAssets(supplierCode: string, availableOnly: boolean): Observable<Asset[]> {
        let url = `${this.config.BaseUrl}Profile/${supplierCode}/Asset`;
        if (availableOnly) {
            url = url + `/Available`;
        }
        return this.http.get<Asset[]>(url, { headers: this.setHeaders() });
    }

    getProcessFlowByToolId(supplierCode: string, toolId: number): Observable<any[]> {
        let url = `${this.config.BaseUrl}Profile/ProcessFlowByTool/${supplierCode}/${toolId}`;
        return this.http.get<any[]>(url, { headers: this.setHeaders() });
    }

    getParameterType(supplierCode: string, subcategoryId: number): Observable<ToolParameterType[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Tool/Subcategory/${subcategoryId}/ParameterType`;
        return this.http.get<ToolParameterType[]>(url, { headers: this.setHeaders() });
    }

    getDeliverableType(supplierCode: string, subcategoryId: number): Observable<ToolDeliverableType[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Tool/Subcategory/${subcategoryId}/DeliverableType`;
        return this.http.get<ToolDeliverableType[]>(url, { headers: this.setHeaders() });
    }

    getCategories(): Observable<ToolCategory[]> {
        const url = `${this.config.BaseUrl}Profile/Tool/Category`;
        return this.http.get<ToolCategory[]>(url, { headers: this.setHeaders() });
    }

    getSubcategories(supplierCode: string, categoryId: number): Observable<ToolSubcategory[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Tool/Category/${categoryId}/Subcategory`;
        return this.http.get<ToolSubcategory[]>(url, { headers: this.setHeaders() });
    }

    postSubCategory(supplierCode: string, model: ToolSubcategory): Observable<ToolSubcategory> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Tool/Subcategory`;
        return this.http.post<ToolSubcategory>(url, model, { headers: this.setHeaders() });
    }

    postParameterType(paramType: ToolParameterType): Observable<ToolParameterType> {
        const url = `${this.config.BaseUrl}Profile/${paramType.supplierCode}/Tool/ParameterType`;
        return this.http.post<ToolParameterType>(url, paramType, { headers: this.setHeaders() });
    }

    getOwnershipType(): Observable<OwnershipType[]> {
        const url = `${this.config.BaseUrl}Profile/OwnershipType`;
        return this.http.get<OwnershipType[]>(url, { headers: this.setHeaders() });
    }

    searchTools(supplierCode: string, toolFilter: ToolFilter): Observable<SearchResult<Tool>> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Tool/Filter`;
        return this.http.post<SearchResult<Tool>>(url, toolFilter, { headers: this.setHeaders() });
    }

    postTool(supplierCode: string, tool: Tool): Observable<Tool> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Tool`;
        return this.http.post<Tool>(url, tool, { headers: this.setHeaders() });
    }

    putTool(supplierCode: string, tool: Tool): Observable<Tool> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Tool`;
        return this.http.put<Tool>(url, tool, { headers: this.setHeaders() });
    }

    deleteTool(supplierCode: string, id: number): Observable<any> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Tool/${id}`;
        return this.http.delete(url, { headers: this.setHeaders() });
    }

    getPickupWarehouses(supplierCode: string): Observable<SupplierAddressPropertyValues[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/PickupWarehouse`;
        return this.http.get<PickupWarehouse[]>(url, { headers: this.setHeaders() }).pipe(
            map(x => {
                if (x && x.length > 0) {
                    return this.pickupToAddressModel(x);
                }
            })
        )
    }

    getWarehouses(supplierCode: string): Observable<SupplierAddressPropertyValues[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Warehouse`;
        return this.http.get<SupplierWarehouse[]>(url, { headers: this.setHeaders() }).pipe(
            map(x => {
                if (x && x.length > 0) {
                    return this.warehouseToAddressModel(x);
                }
            })
        )
    }

    postWarehouse(supplierCode: string, model: SupplierWarehouse): Observable<SupplierAddressPropertyValues[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Warehouse`;
        const modelItems: SupplierWarehouse[] = [];
        return this.http.post<SupplierWarehouse>(url, model, { headers: this.setHeaders() }).pipe(
            map(x => {
                if (x) {
                    modelItems.push(x);
                    return this.warehouseToAddressModel(modelItems);
                }
            })
        );
    }

    putWarehouse(supplierCode: string, model: SupplierWarehouse): Observable<SupplierAddressPropertyValues[]> {
        const modelItems: SupplierWarehouse[] = [];
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Warehouse/${model.id}`;
        return this.http.put<SupplierWarehouse>(url, model, { headers: this.setHeaders() }).pipe(
            map(x => {
                if (x) {
                    modelItems.push(x);
                    return this.warehouseToAddressModel(modelItems);
                }
            })
        );
    }

    deleteWarehouse(supplierCode: string, warehouseId: number): Observable<any> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Warehouse/${warehouseId}`;
        return this.http.delete(url, { headers: this.setHeaders() });
    }

    getCertificationTypes(supplierCode: string): Observable<CertificationType[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/CertificationType`;
        return this.http.get<CertificationType[]>(url, { headers: this.setHeaders() });
    }

    postCertificationType(supplierCode: string, model: CertificationType): Observable<CertificationType> {
        model.type = model.type.toUpperCase();
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/CertificationType`;
        return this.http.post<CertificationType>(url, model, { headers: this.setHeaders() });
    }

    getCertifications(supplierCode: string, locationId: number): Observable<Certification[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/${locationId}/Certification`;
        return this.http.get<Certification[]>(url, { headers: this.setHeaders() });
    }

    postCertification(supplierCode: string, model: Certification): Observable<Certification> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Certification`;
        return this.http.post<Certification>(url, model, { headers: this.setHeaders() });
    }

    putCertification(supplierCode: string, model: Certification): Observable<Certification> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Certification/${model.id}`;
        return this.http.put<Certification>(url, model, { headers: this.setHeaders() });
    }

    deleteCertification(supplierCode: string, certificationId: number): Observable<any> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Certification/${certificationId}`;
        return this.http.delete(url, { headers: this.setHeaders() });
    }

    postProfileAttachment(supplierCode: string, file: File): Observable<ProfileAttachment> {
        const formData: FormData = new FormData();
        formData.append("file", file, file.name);

        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Attachment`;
        return this.http.post<ProfileAttachment>(url, formData, { headers: this.setFileHeaders() });
    }

    deleteProfileAttachment(supplierCode: string, attachmentId: number): Observable<any> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Attachment/${attachmentId}`;
        return this.http.delete(url, { headers: this.setHeaders() });
    }

    downloadProfileAttachment(supplierCode: string, attachmentKey: string): Observable<Blob> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Attachment/${attachmentKey}/File`;
        return this.http.get<Blob>(url, { headers: this.setFileHeaders(), responseType: "Blob" as "json" });
    }

    postCertificationAttachment(supplierCode: string, certificationId: number, file: File): Observable<CertificationAttachment> {
        const formData: FormData = new FormData();
        formData.append("file", file, file.name);

        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Certification/${certificationId}/Attachment`;
        return this.http.post<CertificationAttachment>(url, formData, { headers: this.setFileHeaders() });
    }

    deleteCertificationAttachment(supplierCode: string, attachmentId: number): Observable<any> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Certification/Attachment/${attachmentId}`;
        return this.http.delete(url, { headers: this.setHeaders() });
    }

    downloadCertificationAttachment(supplierCode: string, attachmentKey: string): Observable<Blob> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Certification/Attachment/${attachmentKey}/File`;
        return this.http.get<Blob>(url, { headers: this.setFileHeaders(), responseType: "Blob" as "json" });
    }

    postToolAttachment(supplierCode: string, file: File): Observable<Attachment> {
        const formData: FormData = new FormData();
        formData.append("file", file, file.name);

        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Tool/Attachment`;
        return this.http.post<Attachment>(url, formData, { headers: this.setFileHeaders() });
    }

    downloadToolAttachment(supplierCode: string, attachmentKey: string): Observable<Blob> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Tool/Attachment/${attachmentKey}/File`;
        return this.http.get<Blob>(url, { headers: this.setFileHeaders(), responseType: "Blob" as "json" });
    }

    postGenericAttachment(supplierCode: string, file: File): Observable<Attachment> {
        const formData: FormData = new FormData();
        formData.append("file", file, file.name);

        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Generic/Attachment`;
        return this.http.post<Attachment>(url, formData, { headers: this.setFileHeaders() });
    }

    downloadGenericAttachment(supplierCode: string, attachmentKey: string): Observable<Blob> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Generic/Attachment/${attachmentKey}/File`;
        return this.http.get<Blob>(url, { headers: this.setFileHeaders(), responseType: "Blob" as "json" });
    }

    getPartSupplierDetails(supplierCode: string, partNumbers: string[]): Observable<PartSupplierDetails[]> {
        const url = `${this.config.BaseUrl}Profile/PartSupplierDetails/${supplierCode}`;
        return this.http.post<PartSupplierDetails[]>(url, partNumbers, { headers: this.setHeaders() });
    }

    getPartsBySupplierCode(prefixText: string, supplierCode: string): Observable<Part[]> {
        const url = `${this.config.BaseUrl}Profile/Get/Parts/${supplierCode}?prefixText=${prefixText}`;
        return this.http.get<Part[]>(url, { headers: this.setHeaders() });
    }

    getHistory(supplireCode: string): Observable<any> {
        const url = `${this.config.BaseUrl}Profile/History/${supplireCode}`;
        return this.http.get<any>(url, { headers: this.setHeaders() });
    }

    searchManufacturingTools(supplierCode: string, toolFilter: ToolFilter): Observable<Tool[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Tool/Manufacturing/Filter`;
        return this.http.post<Tool[]>(url, toolFilter, { headers: this.setHeaders() });
    }

    createDefaultParamaters(params: ToolParameterType[]): Observable<ToolParameterType[]> {
        const url = `${this.config.BaseUrl}Profile/Default/Parameters/Set`;
        return this.http.post<ToolParameterType[]>(url, params, { headers: this.setHeaders() });
    }

    createDefaultDeliverables(deliverables: ToolDeliverableType[]): Observable<ToolDeliverableType[]> {
        const url = `${this.config.BaseUrl}Profile/Default/Deliverables/Set`;
        return this.http.post<ToolDeliverableType[]>(url, deliverables, { headers: this.setHeaders() });
    }

    getDefaultParameters(): Observable<ToolParameterType[]> {
        const url = `${this.config.BaseUrl}Profile/Default/Parameters`;
        return this.http.get<ToolParameterType[]>(url, { headers: this.setHeaders() });
    }

    disableDefaultParamaters(param: ToolParameterType): Observable<ToolParameterType> {
        const url = `${this.config.BaseUrl}Profile/Disable/Default/Parameter`;
        return this.http.post<ToolParameterType>(url, param, { headers: this.setHeaders() });
    }

    getDefaultDeliverables(): Observable<ToolDeliverableType[]> {
        const url = `${this.config.BaseUrl}Profile/Default/Deliverables`;
        return this.http.get<ToolDeliverableType[]>(url, { headers: this.setHeaders() });
    }

    disableDefaultDeliverables(deliverable: ToolDeliverableType): Observable<ToolDeliverableType> {
        const url = `${this.config.BaseUrl}Profile/Disable/Default/Deliverables`;
        return this.http.post<ToolDeliverableType>(url, deliverable, { headers: this.setHeaders() });
    }

    getPendingDefaultParameters(): Observable<ToolParameterType[]> {
        const url = `${this.config.BaseUrl}Profile/Pending/Parameters`;
        return this.http.get<ToolParameterType[]>(url, { headers: this.setHeaders() });
    }

    getPendingDefaultDeliverables(): Observable<ToolDeliverableType[]> {
        const url = `${this.config.BaseUrl}Profile/Pending/Deliverables`;
        return this.http.get<ToolDeliverableType[]>(url, { headers: this.setHeaders() });
    }

    approveOrRejectDefaultDeliverables(deliverable: ToolDeliverableType[]): Observable<ToolDeliverableType[]> {
        const url = `${this.config.BaseUrl}Profile/ApproveorReject/Deliverables`;
        return this.http.post<ToolDeliverableType[]>(url, deliverable, { headers: this.setHeaders() });
    }

    approveOrRejectDefaultParameter(deliverable: ToolParameterType[]): Observable<ToolParameterType[]> {
        const url = `${this.config.BaseUrl}Profile/ApproveorReject/Parameters`;
        return this.http.post<ToolParameterType[]>(url, deliverable, { headers: this.setHeaders() });
    }

    getManageDefaultsPermissions(): Observable<boolean> {
        const url = `${this.config.BaseUrl}Profile/Defaults/GetPermissions`;
        return this.http.get<boolean>(url, { headers: this.setHeaders() });
    }

    getSupplierSOS(supplierCode: string): Observable<[string,string]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/SOSUser`;
        return this.http.get<[string,string]>(url, { headers: this.setHeaders() });
    }

    getManufacturingAddress(supplierCode: string): Observable<SupplierAddressPropertyValues[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/ManufacturingAddress`;
        return this.http.get<Location[]>(url, { headers: this.setHeaders() }).pipe(
            map(x => {
                if (x && x.length > 0) {
                    return this.manufacturingToAddressModel(x);
                }
            })
        )
    }

    getParameterList(searchText: string): Observable<ToolParameterType[]> {
        const url = `${this.config.BaseUrl}Profile/parameters?searchText=${searchText}`;
        return this.http.get<ToolParameterType[]>(url, { headers: this.setHeaders() });
    }

    getDeliverablesList(searchText: string): Observable<ToolDeliverableType[]> {
        const url = `${this.config.BaseUrl}Profile/deliverables?searchText=${searchText}`;
        return this.http.get<ToolDeliverableType[]>(url, { headers: this.setHeaders() });
    }

    getSupplierPaymentActiveHold(supplierCode: string): Observable<any>{
        const url = `${this.config.BaseUrl}Profile/GetPaymentActiveHold/${supplierCode}`;
        return this.http.get<any>(url, { headers: this.setHeaders() });
    }

    getPreventativeStatus (supplierCode: string): Observable<any>{
        const url = `${this.config.BaseUrl}Profile/PreventativeInProgress/${supplierCode}`;
        return this.http.get<any>(url, { headers: this.setHeaders() });
    }


    updateLocationContacts(supplierCode:string, contacts:any) {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/PickupWarehouse/Contact`;
        return this.http.put<any>(url, contacts, { headers: this.setHeaders() });
    }

    isSupplierRemitToAddressChina(supplierCode:string) {
        const url = `${this.config.BaseUrl}Profile/IsSupplierRemitToAddressChina/${supplierCode}`;
        return this.http.get<any>(url, { headers: this.setHeaders() });
    }

    getSupplierValidations(supplierCode: string): Observable<SupplierCommunicationValidation> {
      const url = `${this.config.BaseUrl}Profile/GetRequests/${supplierCode}`;
      return this.http.get<any>(url, { headers: this.setHeaders() });
    }


    private manufacturingToAddressModel(manufacturingAddress: Location[]): SupplierAddressPropertyValues[] {
        const resultList: SupplierAddressPropertyValues[] = [];
        if (manufacturingAddress && manufacturingAddress.length > 0) {
            manufacturingAddress.forEach(x => {
                const item = new SupplierAddressPropertyValues();
                item.manufacturingAddressId = x.id;
                item.street1.newValue = x.properties.address;
                item.street2.newValue = "";
                item.city.newValue = x.properties.city;
                item.state.newValue = x.properties.state;
                item.zipCode.newValue = x.properties.zip;
                item.countryCode.newValue = x.properties.country;
                item.types.newValue = ['Manufacturing'];
                item.addressType = 'Manufacturing';

                resultList.push(item);
            });
            return resultList;
        }
    }

    private warehouseToAddressModel(warehousesAddress: SupplierWarehouse[]): SupplierAddressPropertyValues[] {
        const resultList: SupplierAddressPropertyValues[] = [];
        if (warehousesAddress && warehousesAddress.length > 0) {
            warehousesAddress.forEach(x => {
                const warehouseAddress = new SupplierAddressPropertyValues();
                warehouseAddress.warehouseAddressId = x.id;
                warehouseAddress.street1.newValue = x.address;
                warehouseAddress.street2.newValue = x.address2;
                warehouseAddress.city.newValue = x.city;
                warehouseAddress.state.newValue = x.state;
                warehouseAddress.zipCode.newValue = x.zipCode;
                warehouseAddress.countryCode.newValue = x.country;
                warehouseAddress.comment.newValue = 'N/A';
                warehouseAddress.types.newValue = ['Off-Site Part Storage Locations'];
                warehouseAddress.locationName = x.locationName;

                resultList.push(warehouseAddress);
            });
            return resultList;
        }
    }

    private pickupToAddressModel(pickuphouseAddresses: PickupWarehouse[]): SupplierAddressPropertyValues[] {
        const resultList: SupplierAddressPropertyValues[] = [];
        if (pickuphouseAddresses && pickuphouseAddresses.length > 0) {
            pickuphouseAddresses.forEach(x => {
                const pickuphouseAddress = new SupplierAddressPropertyValues();
                pickuphouseAddress.street1.newValue = x.address;
                pickuphouseAddress.city.newValue = x.city;
                pickuphouseAddress.state.newValue = x.state;
                pickuphouseAddress.zipCode.newValue = x.zip;
                pickuphouseAddress.countryCode.newValue = x.country;
                pickuphouseAddress.types.newValue = ['Supplier Ship From Address'];
                pickuphouseAddress.locationName = x.locationName;
                pickuphouseAddress.comment.newValue = "N/A";
                pickuphouseAddress.contacts.newValue = x['contacts'];
                pickuphouseAddress.locationId.newValue = x['locationId']
                resultList.push(pickuphouseAddress);
            });
        }
        return resultList;
    }
    
    checkMFAException(emailID:string): Observable<Boolean> {
        const url = `${this.config.BaseUrl}Profile/MFAExceptionEmails`;
        let params = new HttpParams();
        params = params.set('emailID', emailID);
        return this.http.get<Boolean>(url, { params: params});
    }

    checkIfNameOrTaxChangeIsAllowed(supplierCode: string): Observable<any> {
        const url = `${this.config.BaseUrl}Profile/CheckIfNameOrTaxChangeIsAllowed/${supplierCode}`;
        return this.http.get<any>(url);
    }

    getRequestListBySupplierCode(supplierCode: string): Observable<RequestListItem[]> {
        const url = `${this.config.BaseUrl}Profile/Onboarding/GetAllChangeRequests/${supplierCode}`;
        return this.http.get<RequestListItem[]>(url);
    }

    getRequestByRequestId(requestId: string): Observable<any> {
        const url = `${this.config.BaseUrl}Profile/Onboarding/ChangeRequest/${requestId}`;
        return this.http.get<any>(url);
    }
}
